import * as React from 'react'
import { ReactElement, useState } from 'react'
import { ReplicationDto } from '../../../config/emailConfig'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Destination, IGraphArrayPayload, IGraphPayload } from '../../../context/AxiosContext'
import useAxios from '../../../hooks/useAxios'
import { AxiosError, AxiosInstance } from 'axios'
import { MailFolder } from '@microsoft/microsoft-graph-types'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import {
    Dialog,
    DialogOpenChangeData,
    DialogSurface,
    DialogTrigger,
    ToolbarButton,
    Tooltip,
} from '@fluentui/react-components'
import { EmailToolbarProps } from '../../Toolbar/Email/EmailToolbar'
import { bundleIcon, LinkSquareFilled, LinkSquareRegular } from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import FormContainer from './FormContainer'
import { FileAction } from '../../../config/fileConfig'
import useUser from '../../../hooks/useUser'
import { useTranslation } from 'react-i18next'

export type LinkEmailFolderDialogProps = EmailToolbarProps & {
    currentReplication?: ReplicationDto
}

export type Callback = (folderId: string, fileReference: string) => Promise<void>

export type FormContainerProps = LinkEmailFolderDialogProps & {
    toggleDialog: (open: boolean) => void
    setCallback: (callback: () => Callback) => void
    globalErrors?: NormalizedError[]
}

export type Values = {
    create: boolean
    id: string
    parentFolderId?: string
    name: string
}

const validationSchema = (axios: AxiosInstance) =>
    Yup.object().shape({
        create: Yup.boolean().required(),
        id: Yup.string().when('create', {
            is: false,
            then: schema => schema.required('Er is geen map geselecteerd'),
        }),
        parentFolderId: Yup.string().when('create', {
            is: true,
            then: schema => schema.required('Er is geen hoofdmap geselecteerd'),
        }),
        name: Yup.string().when('create', {
            is: true,
            then: schema =>
                schema.required('Mapnaam is niet ingevuld').test({
                    name: 'folder-exists',
                    test: (value, { parent: { parentFolderId }, createError }) =>
                        parentFolderId &&
                        value &&
                        axios
                            .get<IGraphArrayPayload<MailFolder>>(`/mailFolders/${parentFolderId}/childFolders`, {
                                baseURL: Destination.Email,
                                headers: { SuppressErrors: 'true' },
                                params: {
                                    $filter: `displayName eq '${value}'`,
                                    includeHiddenFolders: true,
                                },
                            })
                            .then(response =>
                                response.data.value?.length === 0
                                    ? Promise.resolve(true)
                                    : Promise.reject(createError({ message: 'De gekozen mapnaam bestaat al' })),
                            )
                            .catch((error: AxiosError) => Promise.reject(createError(error))),
                }),
        }),
    })

const submitNew = (axios: AxiosInstance, values: Values): Promise<string> => {
    return axios
        .post<IGraphPayload<MailFolder>>(
            `/mailFolders/${values.parentFolderId}/childFolders`,
            { displayName: values.name },
            {
                baseURL: Destination.Email,
                headers: { SuppressErrors: 'true' },
            },
        )
        .then(response => response.data.id)
}

const IconComponent = bundleIcon(LinkSquareFilled, LinkSquareRegular)

export default function LinkEmailFolderDialog(props: LinkEmailFolderDialogProps): ReactElement {
    const [open, setOpen] = useState(false)
    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const axios = useAxios()
    const dispatch = useDispatch()
    const [callback, setCallback] = useState<Callback>()
    const { currentMailbox } = useUser()
    const { t: translate } = useTranslation()

    const toggleDialog = (open: boolean) => {
        setOpen(open)
        props.setPreventDefaultEvent(open)
    }

    return (
        <Dialog open={open} onOpenChange={(_, data: DialogOpenChangeData) => toggleDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={translate('email.filing.synchronize.tooltip')} relationship="description" withArrow>
                    <ToolbarButton
                        icon={<IconComponent />}
                        disabled={props.isLoading}
                        hidden={props.selection.getSelectedCount() === 0}
                        onClick={() => toggleDialog(true)}
                    />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <Formik<Values>
                    initialValues={{
                        create: false,
                        id: props.currentReplication?.id,
                        parentFolderId: '',
                        name: '',
                    }}
                    validate={() => setGlobalErrors(undefined)}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validationSchema={validationSchema(axios)}
                    onSubmit={(values: Values) => {
                        setGlobalErrors(undefined)
                        return (values.create ? submitNew(axios, values) : Promise.resolve(values.id))
                            .then(folderId =>
                                axios
                                    .post<void>(
                                        `/email/${currentMailbox}/folder`,
                                        { id: folderId, fileId: props.file.data.id, enabled: true },
                                        { headers: { SuppressErrors: 'true' } },
                                    )
                                    .then(() => folderId),
                            )
                            .then(folderId => {
                                callback?.(folderId, props.file.data.reference)
                                dispatch({
                                    type: FileAction.Get,
                                    payload: axios.get(`/file/${props.file.data.id}`),
                                })
                                toggleDialog(false)
                            })
                            .catch((error: AxiosError) => setGlobalErrors([normalizeAxiosError(error)]))
                    }}
                >
                    <FormContainer
                        setCallback={setCallback}
                        globalErrors={globalErrors}
                        {...props}
                        toggleDialog={toggleDialog}
                    />
                </Formik>
            </DialogSurface>
        </Dialog>
    )
}
